import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Server, Shield, TrendingUp, DollarSign, Play, Eye, Mail, Users, Calendar, ShoppingBag, BarChart3, Radio, Music, ExternalLink, Plus, Send, Image, Clock, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
interface CreatorDashboardProps {
  onBack?: () => void;
}
const trafficData = [{
  name: 'Mon',
  views: 2400,
  streams: 1800
}, {
  name: 'Tue',
  views: 1398,
  streams: 2200
}, {
  name: 'Wed',
  views: 5800,
  streams: 3200
}, {
  name: 'Thu',
  views: 3908,
  streams: 2800
}, {
  name: 'Fri',
  views: 4800,
  streams: 4100
}, {
  name: 'Sat',
  views: 7800,
  streams: 5200
}, {
  name: 'Sun',
  views: 6200,
  streams: 4800
}];
const revenueData = [{
  name: 'Jan',
  streaming: 1200,
  store: 800,
  ads: 400
}, {
  name: 'Feb',
  streaming: 1800,
  store: 1200,
  ads: 600
}, {
  name: 'Mar',
  streaming: 2400,
  store: 900,
  ads: 800
}, {
  name: 'Apr',
  streaming: 3200,
  store: 1500,
  ads: 1000
}, {
  name: 'May',
  streaming: 2800,
  store: 2100,
  ads: 1200
}, {
  name: 'Jun',
  streaming: 4100,
  store: 2800,
  ads: 1400
}];
const emailStats = [{
  name: 'Sent',
  value: 12400
}, {
  name: 'Opened',
  value: 8200
}, {
  name: 'Clicked',
  value: 3100
}];
const scheduledPosts = [{
  platform: 'Instagram',
  content: 'New single announcement 🎵',
  time: 'Today, 6:00 PM',
  status: 'scheduled'
}, {
  platform: 'Twitter/X',
  content: 'Behind the scenes studio session',
  time: 'Tomorrow, 12:00 PM',
  status: 'scheduled'
}, {
  platform: 'TikTok',
  content: 'Dance challenge clip',
  time: 'Wed, 3:00 PM',
  status: 'draft'
}, {
  platform: 'YouTube',
  content: 'Full music video premiere',
  time: 'Fri, 8:00 PM',
  status: 'scheduled'
}];
const storeProducts = [{
  name: 'Limited Edition Hoodie',
  price: 65,
  sold: 142,
  stock: 58
}, {
  name: 'Signed Vinyl LP',
  price: 45,
  sold: 89,
  stock: 11
}, {
  name: 'VIP Meet & Greet Pass',
  price: 150,
  sold: 24,
  stock: 6
}];
const COLORS = ['#FFD700', '#FF0000', '#4F46E5'];
export function CreatorDashboard({
  onBack
}: CreatorDashboardProps) {
  const [activeSection, setActiveSection] = useState('overview');
  const sections = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'hosting',
    label: 'Hosting & Domains'
  }, {
    id: 'streaming',
    label: 'Streaming'
  }, {
    id: 'content',
    label: 'Content & Traffic'
  }, {
    id: 'email',
    label: 'Email & CRM'
  }, {
    id: 'social',
    label: 'Social Scheduler'
  }, {
    id: 'store',
    label: 'E-Commerce'
  }, {
    id: 'revenue',
    label: 'Revenue'
  }];
  return <div className="min-h-screen bg-[#0D0D0D] text-white font-['Montserrat']" data-id="element-1476">
      {/* Top Bar */}
      <header className="bg-[#0D0D0D]/90 backdrop-blur-xl border-b border-white/5 px-6 py-3 sticky top-0 z-50" data-id="element-1477">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between" data-id="element-1478">
          <div className="flex items-center gap-4" data-id="element-1479">
            {onBack && <button onClick={onBack} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-1480">
                <ArrowLeft size={20} className="text-gray-400" data-id="element-1481" />
              </button>}
            <div className="flex items-center gap-2" data-id="element-1482">
              <div className="w-8 h-8 bg-[#FFD700] rounded flex items-center justify-center" data-id="element-1483">
                <span className="text-black font-['Archivo_Black'] text-lg" data-id="element-1484">
                  H
                </span>
              </div>
              <span className="font-['Archivo_Black'] text-lg tracking-tighter uppercase" data-id="element-1485">
                Creator Dashboard
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-1486">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-id="element-1487">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-1488"></div>
              <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest" data-id="element-1489">
                All Systems Online
              </span>
            </div>
            <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest hover:bg-white transition-all" data-id="element-1490">
              Upgrade Plan
            </button>
          </div>
        </div>
      </header>

      {/* Section Tabs */}
      <div className="border-b border-white/5 px-6 bg-[#0D0D0D]/50 backdrop-blur-sm sticky top-[57px] z-40" data-id="element-1491">
        <div className="max-w-[1800px] mx-auto flex gap-1 overflow-x-auto py-2" data-id="element-1492">
          {sections.map(s => <button key={s.id} onClick={() => setActiveSection(s.id)} className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-[#FFD700] text-black' : 'text-gray-500 hover:text-white hover:bg-white/5'}`} data-id="element-1493">
              {s.label}
            </button>)}
        </div>
      </div>

      <main className="max-w-[1800px] mx-auto p-6 space-y-6" data-id="element-1494">
        {/* Overview Section */}
        {activeSection === 'overview' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1495">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1496">
              {[{
            label: 'Monthly Visitors',
            value: '48.2K',
            change: '+12.4%',
            icon: Eye,
            color: 'text-[#FFD700]'
          }, {
            label: 'Stream Revenue',
            value: '$4,120',
            change: '+24.1%',
            icon: Play,
            color: 'text-emerald-500'
          }, {
            label: 'Store Sales',
            value: '$2,840',
            change: '+8.7%',
            icon: ShoppingBag,
            color: 'text-blue-500'
          }, {
            label: 'Email Subscribers',
            value: '12.4K',
            change: '+340',
            icon: Mail,
            color: 'text-purple-500'
          }].map((stat, i) => <div key={i} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1497">
                  <div className="flex items-center justify-between mb-4" data-id="element-1498">
                    <stat.icon size={20} className={stat.color} data-id="element-1499" />
                    <span className="text-[10px] font-bold text-emerald-500 uppercase" data-id="element-1500">
                      {stat.change}
                    </span>
                  </div>
                  <p className="text-2xl font-['Archivo_Black'] tracking-tight" data-id="element-1501">
                    {stat.value}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-1502">
                    {stat.label}
                  </p>
                </div>)}
            </div>

            {/* Traffic + Revenue Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1503">
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1504">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1505">
                  Content Traffic (7 Days)
                </h3>
                <div className="h-[250px]" data-id="element-1506">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1507">
                    <AreaChart data={trafficData} data-id="element-1508">
                      <defs data-id="element-1509">
                        <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-1510">
                          <stop offset="5%" stopColor="#FFD700" stopOpacity={0.2} data-id="element-1511" />
                          <stop offset="95%" stopColor="#FFD700" stopOpacity={0} data-id="element-1512" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1513" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1514" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1515" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }} data-id="element-1516" />
                      <Area type="monotone" dataKey="views" stroke="#FFD700" strokeWidth={2} fill="url(#goldGrad)" data-id="element-1517" />
                      <Area type="monotone" dataKey="streams" stroke="#FF0000" strokeWidth={2} fill="transparent" strokeDasharray="5 5" data-id="element-1518" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1519">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1520">
                  Revenue Breakdown (6 Months)
                </h3>
                <div className="h-[250px]" data-id="element-1521">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1522">
                    <BarChart data={revenueData} data-id="element-1523">
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1524" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1525" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1526" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }} data-id="element-1527" />
                      <Bar dataKey="streaming" fill="#FFD700" radius={[4, 4, 0, 0]} data-id="element-1528" />
                      <Bar dataKey="store" fill="#FF0000" radius={[4, 4, 0, 0]} data-id="element-1529" />
                      <Bar dataKey="ads" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-1530" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-6 mt-4" data-id="element-1531">
                  {[{
                label: 'Streaming',
                color: 'bg-[#FFD700]'
              }, {
                label: 'Store',
                color: 'bg-[#FF0000]'
              }, {
                label: 'Ads',
                color: 'bg-[#4F46E5]'
              }].map((l, i) => <div key={i} className="flex items-center gap-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-1532">
                      <div className={`w-2 h-2 rounded-full ${l.color}`} data-id="element-1533"></div>
                      {l.label}
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Hosting & Domains */}
        {activeSection === 'hosting' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1534">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1535">
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1536">
                <div className="flex items-center gap-3 mb-6" data-id="element-1537">
                  <Server size={20} className="text-[#FFD700]" data-id="element-1538" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1539">
                    Hosting Status
                  </h3>
                </div>
                <div className="space-y-4" data-id="element-1540">
                  {[{
                label: 'Plan',
                value: 'Creator Pro ($149/mo)'
              }, {
                label: 'Storage',
                value: '45 GB / 100 GB'
              }, {
                label: 'Bandwidth',
                value: '820 GB / Unlimited'
              }, {
                label: 'Uptime',
                value: '99.99%'
              }].map((item, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-1541">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-1542">
                        {item.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-1543">{item.value}</span>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1544">
                <div className="flex items-center gap-3 mb-6" data-id="element-1545">
                  <Globe size={20} className="text-[#FFD700]" data-id="element-1546" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1547">
                    Domains
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-1548">
                  {[{
                domain: 'artistname.com',
                status: 'Active',
                ssl: true
              }, {
                domain: 'artistname.store',
                status: 'Active',
                ssl: true
              }, {
                domain: 'artistname.live',
                status: 'Pending',
                ssl: false
              }].map((d, i) => <div key={i} className="flex items-center justify-between p-3 bg-black/40 rounded-lg border border-white/5" data-id="element-1549">
                      <div data-id="element-1550">
                        <p className="text-sm font-bold" data-id="element-1551">{d.domain}</p>
                        <div className="flex items-center gap-2 mt-1" data-id="element-1552">
                          {d.ssl && <span className="text-[8px] font-bold text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded uppercase" data-id="element-1553">
                              SSL+
                            </span>}
                          <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${d.status === 'Active' ? 'text-emerald-500 bg-emerald-500/10' : 'text-amber-500 bg-amber-500/10'}`} data-id="element-1554">
                            {d.status}
                          </span>
                        </div>
                      </div>
                      <ExternalLink size={14} className="text-gray-600" data-id="element-1555" />
                    </div>)}
                </div>
                <button className="mt-4 w-full py-2.5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors flex items-center justify-center gap-2" data-id="element-1556">
                  <Plus size={14} data-id="element-1557" /> Add Domain
                </button>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1558">
                <div className="flex items-center gap-3 mb-6" data-id="element-1559">
                  <Shield size={20} className="text-emerald-500" data-id="element-1560" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1561">
                    SSL & Security
                  </h3>
                </div>
                <div className="text-center py-8" data-id="element-1562">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4" data-id="element-1563">
                    <Shield size={32} className="text-emerald-500" data-id="element-1564" />
                  </div>
                  <p className="text-2xl font-['Archivo_Black']" data-id="element-1565">SSL+ Active</p>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-2" data-id="element-1566">
                    Wildcard Certificate
                  </p>
                  <p className="text-[10px] text-gray-600 mt-1" data-id="element-1567">
                    Expires: Dec 2025
                  </p>
                </div>
                <div className="space-y-2 mt-4" data-id="element-1568">
                  {['DDoS Protection', 'Firewall Active', 'Daily Backups'].map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1569">
                        <CheckCircle2 size={14} className="text-emerald-500" data-id="element-1570" />
                        {f}
                      </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Streaming */}
        {activeSection === 'streaming' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1571">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1572">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1573">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1574">
                  Monetization Tracking
                </h3>
                <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-1575">
                  {[{
                label: 'Total Streams',
                value: '284K',
                sub: 'This month'
              }, {
                label: 'Revenue',
                value: '$4,120',
                sub: '+24% vs last month'
              }, {
                label: 'Avg. Per Stream',
                value: '$0.0145',
                sub: 'Above industry avg'
              }].map((s, i) => <div key={i} className="bg-black/40 rounded-lg p-4 border border-white/5" data-id="element-1576">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-1577">
                        {s.label}
                      </p>
                      <p className="text-xl font-['Archivo_Black'] mt-1" data-id="element-1578">
                        {s.value}
                      </p>
                      <p className="text-[10px] text-gray-600 mt-1" data-id="element-1579">{s.sub}</p>
                    </div>)}
                </div>
                <div className="h-[200px]" data-id="element-1580">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1581">
                    <LineChart data={trafficData} data-id="element-1582">
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1583" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1584" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1585" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }} data-id="element-1586" />
                      <Line type="monotone" dataKey="streams" stroke="#FFD700" strokeWidth={3} dot={false} data-id="element-1587" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1588">
                <div className="flex items-center gap-3 mb-6" data-id="element-1589">
                  <Radio size={20} className="text-[#FF0000]" data-id="element-1590" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1591">
                    Live Stream
                  </h3>
                </div>
                <div className="text-center py-8" data-id="element-1592">
                  <div className="w-16 h-16 rounded-full bg-[#FF0000]/20 border border-[#FF0000]/30 flex items-center justify-center mx-auto mb-4" data-id="element-1593">
                    <Play size={24} className="text-[#FF0000] ml-0.5" data-id="element-1594" />
                  </div>
                  <p className="font-bold" data-id="element-1595">Ready to Go Live</p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1596">
                    Wowza Integration Active
                  </p>
                </div>
                <button className="w-full py-3 bg-[#FF0000] text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-red-700 transition-colors" data-id="element-1597">
                  Start Live Stream
                </button>
              </div>
            </div>
          </motion.div>}

        {/* Content & Traffic */}
        {activeSection === 'content' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1598">
            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1599">
              <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1600">
                Content Performance & Traffic Sources
              </h3>
              <div className="h-[300px]" data-id="element-1601">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1602">
                  <AreaChart data={trafficData} data-id="element-1603">
                    <defs data-id="element-1604">
                      <linearGradient id="goldGrad2" x1="0" y1="0" x2="0" y2="1" data-id="element-1605">
                        <stop offset="5%" stopColor="#FFD700" stopOpacity={0.3} data-id="element-1606" />
                        <stop offset="95%" stopColor="#FFD700" stopOpacity={0} data-id="element-1607" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1608" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1609" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1610" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }} data-id="element-1611" />
                    <Area type="monotone" dataKey="views" stroke="#FFD700" strokeWidth={2} fill="url(#goldGrad2)" name="Page Views" data-id="element-1612" />
                    <Area type="monotone" dataKey="streams" stroke="#FF0000" strokeWidth={2} fill="transparent" strokeDasharray="5 5" name="Stream Views" data-id="element-1613" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1614">
              {[{
            source: 'Direct',
            visits: '18.2K',
            pct: '38%'
          }, {
            source: 'Social Media',
            visits: '12.4K',
            pct: '26%'
          }, {
            source: 'Search (SEO)',
            visits: '10.1K',
            pct: '21%'
          }, {
            source: 'Referral',
            visits: '7.5K',
            pct: '15%'
          }].map((s, i) => <div key={i} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-id="element-1615">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-1616">
                    {s.source}
                  </p>
                  <p className="text-xl font-['Archivo_Black'] mt-1" data-id="element-1617">
                    {s.visits}
                  </p>
                  <p className="text-xs text-[#FFD700] font-bold mt-1" data-id="element-1618">
                    {s.pct} of total
                  </p>
                </div>)}
            </div>
          </motion.div>}

        {/* Email & CRM */}
        {activeSection === 'email' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1619">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1620">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1621">
                <div className="flex items-center justify-between mb-6" data-id="element-1622">
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1623">
                    Email Campaigns
                  </h3>
                  <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest" data-id="element-1624">
                    <Plus size={14} className="inline mr-1" data-id="element-1625" /> New Campaign
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-1626">
                  {emailStats.map((s, i) => <div key={i} className="bg-black/40 rounded-lg p-4 border border-white/5 text-center" data-id="element-1627">
                      <p className="text-2xl font-['Archivo_Black']" data-id="element-1628">
                        {(s.value / 1000).toFixed(1)}K
                      </p>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-1629">
                        {s.name}
                      </p>
                    </div>)}
                </div>
                <div className="space-y-3" data-id="element-1630">
                  {[{
                subject: 'New Single Drop — Listen Now 🎵',
                sent: '12.4K',
                opened: '67%',
                date: 'Oct 12'
              }, {
                subject: 'Tour Dates Announced!',
                sent: '11.8K',
                opened: '72%',
                date: 'Oct 5'
              }, {
                subject: 'Exclusive Merch Pre-Sale',
                sent: '10.2K',
                opened: '58%',
                date: 'Sep 28'
              }].map((c, i) => <div key={i} className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-white/5" data-id="element-1631">
                      <div data-id="element-1632">
                        <p className="text-sm font-bold" data-id="element-1633">{c.subject}</p>
                        <p className="text-[10px] text-gray-500 font-bold mt-1" data-id="element-1634">
                          {c.date} • Sent to {c.sent}
                        </p>
                      </div>
                      <span className="text-xs font-bold text-[#FFD700]" data-id="element-1635">
                        {c.opened} open rate
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1636">
                <div className="flex items-center gap-3 mb-6" data-id="element-1637">
                  <Users size={20} className="text-purple-500" data-id="element-1638" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1639">CRM</h3>
                </div>
                <div className="space-y-4" data-id="element-1640">
                  {[{
                label: 'Total Contacts',
                value: '12,400'
              }, {
                label: 'VIP Fans',
                value: '842'
              }, {
                label: 'New This Month',
                value: '+340'
              }, {
                label: 'Engagement Rate',
                value: '67%'
              }].map((s, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-1641">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-1642">
                        {s.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-1643">{s.value}</span>
                    </div>)}
                </div>
                <button className="mt-6 w-full py-2.5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors" data-id="element-1644">
                  Manage Contacts
                </button>
              </div>
            </div>
          </motion.div>}

        {/* Social Scheduler */}
        {activeSection === 'social' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1645">
            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1646">
              <div className="flex items-center justify-between mb-6" data-id="element-1647">
                <h3 className="font-bold uppercase tracking-wider" data-id="element-1648">
                  Social Media Post Scheduler
                </h3>
                <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-1649">
                  <Plus size={14} data-id="element-1650" /> Schedule Post
                </button>
              </div>
              <div className="space-y-3" data-id="element-1651">
                {scheduledPosts.map((post, i) => <div key={i} className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-white/5 hover:border-[#FFD700]/20 transition-colors" data-id="element-1652">
                    <div className="flex items-center gap-4" data-id="element-1653">
                      <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center" data-id="element-1654">
                        <span className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-1655">
                          {post.platform.slice(0, 2)}
                        </span>
                      </div>
                      <div data-id="element-1656">
                        <p className="text-sm font-bold" data-id="element-1657">{post.content}</p>
                        <div className="flex items-center gap-2 mt-1" data-id="element-1658">
                          <span className="text-[10px] font-bold text-gray-500" data-id="element-1659">
                            {post.platform}
                          </span>
                          <span className="text-[10px] text-gray-600" data-id="element-1660">•</span>
                          <Clock size={10} className="text-gray-600" data-id="element-1661" />
                          <span className="text-[10px] text-gray-500" data-id="element-1662">
                            {post.time}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${post.status === 'scheduled' ? 'bg-[#FFD700]/10 text-[#FFD700]' : 'bg-gray-800 text-gray-500'}`} data-id="element-1663">
                      {post.status}
                    </span>
                  </div>)}
              </div>
            </div>
          </motion.div>}

        {/* E-Commerce */}
        {activeSection === 'store' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1664">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1665">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1666">
                <div className="flex items-center justify-between mb-6" data-id="element-1667">
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1668">
                    Store Products
                  </h3>
                  <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-1669">
                    <Plus size={14} data-id="element-1670" /> Add Product
                  </button>
                </div>
                <table className="w-full" data-id="element-1671">
                  <thead data-id="element-1672">
                    <tr className="text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-white/5" data-id="element-1673">
                      <th className="text-left py-3" data-id="element-1674">Product</th>
                      <th className="text-left py-3" data-id="element-1675">Price</th>
                      <th className="text-left py-3" data-id="element-1676">Sold</th>
                      <th className="text-left py-3" data-id="element-1677">Stock</th>
                      <th className="text-right py-3" data-id="element-1678">Revenue</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1679">
                    {storeProducts.map((p, i) => <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors" data-id="element-1680">
                        <td className="py-4 text-sm font-bold" data-id="element-1681">{p.name}</td>
                        <td className="py-4 text-sm text-gray-400" data-id="element-1682">
                          ${p.price}
                        </td>
                        <td className="py-4 text-sm text-gray-400" data-id="element-1683">{p.sold}</td>
                        <td className="py-4" data-id="element-1684">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.stock < 15 ? 'bg-[#FF0000]/10 text-[#FF0000]' : 'bg-emerald-500/10 text-emerald-500'}`} data-id="element-1685">
                            {p.stock} left
                          </span>
                        </td>
                        <td className="py-4 text-sm font-bold text-right text-[#FFD700]" data-id="element-1686">
                          ${(p.price * p.sold).toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1687">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1688">
                  Store Stats
                </h3>
                <div className="space-y-4" data-id="element-1689">
                  {[{
                label: 'Total Revenue',
                value: '$18,240'
              }, {
                label: 'Orders This Month',
                value: '255'
              }, {
                label: 'Avg Order Value',
                value: '$71.50'
              }, {
                label: 'Conversion Rate',
                value: '3.2%'
              }].map((s, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-1690">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-1691">
                        {s.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-1692">{s.value}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Revenue */}
        {activeSection === 'revenue' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1693">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1694">
              {[{
            label: 'Streaming',
            value: '$4,120',
            icon: Music
          }, {
            label: 'Storefront',
            value: '$2,840',
            icon: ShoppingBag
          }, {
            label: 'Ad Revenue',
            value: '$1,200',
            icon: BarChart3
          }, {
            label: 'Total Revenue',
            value: '$8,160',
            icon: DollarSign
          }].map((s, i) => <div key={i} className={`border border-white/5 rounded-xl p-6 ${i === 3 ? 'bg-[#FFD700]/10 border-[#FFD700]/20' : 'bg-[#1A1A1A]'}`} data-id="element-1695">
                  <s.icon size={20} className={i === 3 ? 'text-[#FFD700]' : 'text-gray-500'} data-id="element-1696" />
                  <p className="text-2xl font-['Archivo_Black'] mt-3" data-id="element-1697">
                    {s.value}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-1698">
                    {s.label}
                  </p>
                </div>)}
            </div>

            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1699">
              <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1700">
                Revenue Over Time
              </h3>
              <div className="h-[300px]" data-id="element-1701">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1702">
                  <BarChart data={revenueData} data-id="element-1703">
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1704" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1705" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1706" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }} data-id="element-1707" />
                    <Bar dataKey="streaming" stackId="a" fill="#FFD700" radius={[0, 0, 0, 0]} data-id="element-1708" />
                    <Bar dataKey="store" stackId="a" fill="#FF0000" data-id="element-1709" />
                    <Bar dataKey="ads" stackId="a" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-1710" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>}
      </main>
    </div>;
}